/**
 * The picture of ONE variant in the colorway it actually sews.
 *
 * Jake, 2026-10-22: the per-color card on the Placements page must show the
 * DST/EMB design in the card's SELECTED colorway, not the default (black)
 * stitch rendering. The default comes from the version's preview sidecar
 * (variantThumbnail.js), which has no colorway axis; the colorway picture
 * comes from GET /api/placements/<p>/variants/<v>/colorway_thumbnail, a
 * Wilcom recolor the server renders once per (file, threads) and caches in
 * S3 (api/src/services/colorway_thumbnail.py). No Artifi spend.
 *
 * The server derives the colorway from the variant's own snapshot; the
 * threads here only key the CLIENT cache, so a re-bind (new snapshot) is a
 * new key and two colorways of one design can never share a picture.
 *
 * States: 'none' (nothing to ask for), 'loading', 'ready', 'failed'. Only
 * 'ready' and the server's 'none' are cached for the page's lifetime; a
 * failure is retried on the next mount, matching the server, which does not
 * cache failures either.
 */
import { ref, watch } from "vue";
import api from "@/services/api";

/** key -> Promise<{state, url}> */
const _cache = new Map();

/** Test seam: forget every cached picture. */
export function _resetColorwayThumbnailCache() {
    _cache.clear();
}

// The artwork is part of the key: the server answers `none` when a variant
// shows an artwork other than the decoration's, so a Replace that keeps the
// variant id and threads must not reuse the old design's cached picture.
function cacheKey({ placementId, variantId, artworkFileId, threads }) {
    return `${placementId}:${variantId}:${artworkFileId}:${JSON.stringify(threads ?? null)}`;
}

function fetchThumbnail(source) {
    const key = cacheKey(source);
    const hit = _cache.get(key);
    if (hit) return hit;
    const pending = api
        .get(
            `/api/placements/${source.placementId}/variants/${source.variantId}/colorway_thumbnail`,
        )
        .then((resp) => {
            const data = resp?.data ?? {};
            if (data.state === "ready" && typeof data.preview_url === "string" && data.preview_url) {
                return { state: "ready", url: data.preview_url };
            }
            if (data.state === "none") return { state: "none", url: null };
            _cache.delete(key);
            return { state: "failed", url: null };
        })
        .catch(() => {
            _cache.delete(key);
            return { state: "failed", url: null };
        });
    _cache.set(key, pending);
    return pending;
}

/**
 * @param {import('vue').Ref<null|{placementId: string, variantId: string, artworkFileId: string, threads: Array}>} source
 *   null when the card has nothing to ask for (unbound, raster, unsaved).
 * @returns {{ url: import('vue').Ref<string|null>, state: import('vue').Ref<string>, markBroken: Function }}
 */
export function useVariantColorwayThumbnail(source) {
    const url = ref(null);
    const state = ref("none");
    let token = 0;

    watch(
        () => (source.value ? cacheKey(source.value) : null),
        (key) => {
            const mine = ++token;
            url.value = null;
            if (!key) {
                state.value = "none";
                return;
            }
            state.value = "loading";
            fetchThumbnail(source.value).then((result) => {
                // A newer snapshot owns the card now; drop this answer.
                if (mine !== token) return;
                url.value = result.url;
                state.value = result.state;
            });
        },
        { immediate: true },
    );

    // The <img> could not load the URL the server named: show the default and
    // forget the entry so the next mount asks again.
    function markBroken() {
        if (source.value) _cache.delete(cacheKey(source.value));
        url.value = null;
        state.value = "failed";
    }

    return { url, state, markBroken };
}
